import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useSelector } from "react-redux";

import { Box, Stack, Typography } from "@mui/material";

import TemporaryNewSiteConfigurations from "./TemporaryNewSiteConfigurations";
import RequiredConfigurationsItems, {
  DuplicateSiteRequiredType,
  RequiredSiteFieldsValidationErrors
} from "../siteOperations/RequiredConfigurationsItems";
import { getSiteOperationTempData } from "../../redux/selectors/siteSelectors";
import {
  buildConfigurationTypeTree,
  getAllNestedSubConfigurations,
  getFormattedRequiredConfigurationItemsList
} from "../../lib/siteHelpers";
import CustomButton from "../CustomButton";
import {
  ConfigurationNameWithEditButton,
  LoadingSpinner
} from "../site/ConfigurationItems";
import { usePreservedRef } from "../../usePreservedRef";
import { delay } from "../../lib/helpers";
import usePrevious from "../../hooks/usePrevious";
import {
  ConfirmActionDialogOptions,
  useConfirmActionDialog
} from "../useConfirmActionDialog";
import EditSiteSectionModal, {
  EditSiteSectionModalForm
} from "../site/EditSiteSectionModal";
import siteApi, {
  usePostNewSiteConfigurationMutation
} from "../../redux/api/site";
import { getSelectedDrawersData } from "../../redux/selectors/drawerSelectors";
import { useAppDispatch } from "../../store";
import { updateUserMessagesList } from "../messagePopper/messagePopperSlice";

export type AdditionalRefMethods = {
  getData: () => {
    [configItemId in string]: DuplicateSiteRequiredType;
  };
  validateCurrentData: () => Promise<{
    hasErrors: boolean;
    errorList: {
      errorMessage: string;
      id: string;
    }[];
  }>;
  validationErrors?: RequiredSiteFieldsValidationErrors;
  isChildConfigurationRehydrated?: boolean;
};
export type TemporaryConfigurationMenuStatusType = {
  [id in string]: {
    id: string;
    isOpen: boolean;
    isValid: boolean;
    hasVisitedConfigurationAtLeastOnce: boolean;
    isChildConfigurationRehydrated: boolean;
    numberOfErrors?: number;
    configurationDetails: {
      name: string;
      description: string;
      notes: string | null;
    };
    configurationErrorMessage: string;
  };
};

const TemporaryNewConfigurationData = ({
  currentSiteConfigurations,
  onClose
}: {
  currentSiteConfigurations: SiteConfigurationResponseType;
  onClose: () => void;
}) => {
  const {
    getValue,
    getRef,
    register,
    unregister,
    clearAllRefs,
    setCustomPropertyOnRef
  } = usePreservedRef<AdditionalRefMethods>();
  const dispatch = useAppDispatch();

  const [isBtnLoading, setIsBtnLoading] = useState(false);
  const [isEditSectionModalOpen, updateSiteSectionModalStatus] =
    useState(false);
  const { openDialog, resetDialogState: closeDialog } =
    useConfirmActionDialog();

  const siteOperationData = useSelector(getSiteOperationTempData);
  const initialRequestedConfigurationTypeDetails =
    siteOperationData.requestNewSiteConfigurationTempData.configurationType;
  const newRequestedConfiguration =
    siteOperationData?.requestNewSiteConfigurationTempData
      ?.newRequestedConfiguration || null;

  const selectedSiteId = useSelector(getSelectedDrawersData)?.site?.id;

  const [currentSelectedConfigurationType, updateSelectedConfigurationType] =
    useState<string>("");
  const [temporaryConfigurationMenuStatus, setSelectedConfiguration] =
    useState<TemporaryConfigurationMenuStatusType | null>(null);
  const currentConfigurationMenuStatus = useMemo(
    () => temporaryConfigurationMenuStatus?.[currentSelectedConfigurationType],
    [temporaryConfigurationMenuStatus, currentSelectedConfigurationType]
  );
  const prevConfigurationMenuStatus = usePrevious(
    currentConfigurationMenuStatus
  );
  const currentConfigurationDetails =
    currentConfigurationMenuStatus?.configurationDetails;
  const hasValidatedcurrentConfigurationRef = useRef<Record<string, boolean>>(
    {}
  );
  const isValidatingCurrentConfigurationRef = useRef<boolean>(false);

  const [
    createNewConfiguration,
    { isLoading: isCreatingNewConfigurationLoading }
  ] = usePostNewSiteConfigurationMutation();

  const checkIfAllSectionsAreValid = useMemo(() => {
    if (!temporaryConfigurationMenuStatus) return false;
    return Object.values(temporaryConfigurationMenuStatus).every((item) => {
      return (
        item.isValid &&
        item.hasVisitedConfigurationAtLeastOnce &&
        (item.numberOfErrors ?? 0) === 0 &&
        (item.configurationErrorMessage || "")?.length === 0
      );
    });
  }, [temporaryConfigurationMenuStatus]);

  const allConfigurationTypes = useMemo(() => {
    if (!newRequestedConfiguration) return [];
    const nestedConfigurations = getAllNestedSubConfigurations(
      newRequestedConfiguration
    );
    return nestedConfigurations;
  }, [newRequestedConfiguration, getAllNestedSubConfigurations]);

  const updateTemporaryConfigurationMenuStatusById = (
    id: string,
    propertiesToUpdate: {
      isValid?: boolean;
      hasVisitedConfigurationAtLeastOnce?: boolean;
      isChildConfigurationRehydrated?: boolean;
      isOpen?: boolean;
      numberOfErrors?: number;
      configurationDetails?: {
        name: string;
        description: string;
        notes: string | null;
      };
      configurationErrorMessage?: string;
    }
  ) => {
    const {
      isValid,
      hasVisitedConfigurationAtLeastOnce,
      isChildConfigurationRehydrated,
      isOpen,
      numberOfErrors,
      configurationDetails,
      configurationErrorMessage
    } = propertiesToUpdate;
    setSelectedConfiguration((prevState) => {
      const prevExistingStateForId = prevState?.[id] ?? {
        id,
        isOpen: true,
        isValid: false,
        hasVisitedConfigurationAtLeastOnce: false,
        isChildConfigurationRehydrated: false,
        configurationDetails: {
          name: "",
          description: "",
          notes: null
        },
        configurationErrorMessage: "",
        numberOfErrors: 0
      };

      return {
        ...prevState,
        [id]: {
          ...prevExistingStateForId,
          isValid: isValid ?? prevExistingStateForId.isValid,
          hasVisitedConfigurationAtLeastOnce:
            hasVisitedConfigurationAtLeastOnce ??
            prevExistingStateForId.hasVisitedConfigurationAtLeastOnce,
          isChildConfigurationRehydrated:
            isChildConfigurationRehydrated ??
            prevExistingStateForId.isChildConfigurationRehydrated,
          isOpen: isOpen ?? prevExistingStateForId.isOpen,
          numberOfErrors:
            numberOfErrors ?? prevExistingStateForId.numberOfErrors,
          configurationDetails:
            configurationDetails ?? prevExistingStateForId.configurationDetails,
          configurationErrorMessage:
            configurationErrorMessage ??
            prevExistingStateForId.configurationErrorMessage
        }
      };
    });
  };

  const validateConfigurationDetailsById = useCallback(
    (
      selectedId: string
    ): {
      hasErrors: boolean;
      configurationErrorMessage: string;
    } => {
      const configurationStatus =
        temporaryConfigurationMenuStatus?.[selectedId];

      if (!configurationStatus) {
        return {
          hasErrors: true,
          configurationErrorMessage: ""
        };
      }

      const doesConfigurationHaveValidDetails =
        !!configurationStatus.configurationDetails.name &&
        !!configurationStatus.configurationDetails.description;

      return {
        hasErrors: !doesConfigurationHaveValidDetails,
        configurationErrorMessage: doesConfigurationHaveValidDetails
          ? ""
          : "Configuration name and description are required fields."
      };
    },
    [temporaryConfigurationMenuStatus, allConfigurationTypes]
  );

  const validateConfigurationItems = async (
    selectedId: string
  ): Promise<{
    hasErrors: boolean;
    numberOfErrors: number;
  }> => {
    const childRef = getRef(selectedId);
    if (!childRef.current)
      return {
        hasErrors: false,
        numberOfErrors: 0
      };

    try {
      // Trigger validation and await the child's async delay.
      const validationResult = await childRef.current.validateCurrentData();

      // Optionally wait one tick more if needed
      await delay(0);
      return {
        hasErrors: validationResult.hasErrors,
        numberOfErrors: validationResult.errorList.length
      };
    } catch {
      return {
        hasErrors: true,
        numberOfErrors: 0
      };
    }
  };

  const checkAndUpdateConfigurationValidationStatus = useCallback(
    async (specificConfigTypeId: string) => {
      if (isValidatingCurrentConfigurationRef.current) return;

      isValidatingCurrentConfigurationRef.current = true;
      try {
        const { hasErrors: hasConfigurationItemsErrors, numberOfErrors } =
          await validateConfigurationItems(specificConfigTypeId);
        const {
          hasErrors: hasConfigurationDetailsErrors,
          configurationErrorMessage
        } = validateConfigurationDetailsById(specificConfigTypeId);
        const hasErrors =
          hasConfigurationItemsErrors || hasConfigurationDetailsErrors;

        updateTemporaryConfigurationMenuStatusById(specificConfigTypeId, {
          isValid: !hasErrors,
          hasVisitedConfigurationAtLeastOnce: true,
          numberOfErrors,
          configurationErrorMessage: configurationErrorMessage || ""
        });
        return !hasErrors;
      } finally {
        isValidatingCurrentConfigurationRef.current = false;
      }
    },
    [
      updateTemporaryConfigurationMenuStatusById,
      validateConfigurationDetailsById,
      temporaryConfigurationMenuStatus,
      updateTemporaryConfigurationMenuStatusById
    ]
  );

  const onMounted = (configTypeId: string) => {
    updateTemporaryConfigurationMenuStatusById(configTypeId, {
      isChildConfigurationRehydrated: true,
      hasVisitedConfigurationAtLeastOnce: true
    });
  };

  const saveConfigurationChages = (data: EditSiteSectionModalForm) => {
    if (!currentConfigurationMenuStatus) return;

    updateTemporaryConfigurationMenuStatusById(
      currentSelectedConfigurationType,
      {
        configurationDetails: data
      }
    );
  };

  const requiredConfigurationItemsByConfigurationType = useMemo(() => {
    if (!allConfigurationTypes.length) return {};
    return allConfigurationTypes.reduce<{
      [configurationTypeId in string]: JSX.Element;
    }>((acc, configuration, i) => {
      const currentTemporaryConfigurationMenuTypeStatus =
        temporaryConfigurationMenuStatus?.[configuration.configurationTypeId];
      return {
        ...acc,
        [configuration.configurationTypeId]: (
          <>
            {currentTemporaryConfigurationMenuTypeStatus && (
              <ConfigurationNameWithEditButton
                onClick={() => updateSiteSectionModalStatus(true)}
                name={
                  currentTemporaryConfigurationMenuTypeStatus
                    .configurationDetails.name
                }
                shouldRenderEditBtn
              />
            )}
            <RequiredConfigurationsItems
              key={i}
              //@ts-ignore
              siteOperationData={{
                ...siteOperationData.generalSiteInformation,
                siteConfigurationsByMenuType: currentSiteConfigurations,
                allSiteConfigurationsByConfigurationId: {},
                requiredSiteConfigurationItems: configuration.configurationItems
              }}
              handleModalClose={onClose}
              submitBtnName="Submit changes"
              onSubmitRequiredFieldChanges={(
                _pendingChanges,
                _updatedSiteData
              ) => {}}
              disableActionButtons
              additionaParentRef={
                getRef(configuration.configurationTypeId) as
                  | React.RefObject<AdditionalRefMethods>
                  | undefined
              }
              onRegister={(ref: AdditionalRefMethods) => {
                register(configuration.configurationTypeId, ref);
              }}
              onUnRegister={(additionalPropertyToChange?: {
                propertyName: string;
                value: string | number | boolean | null;
              }) => {
                unregister(
                  configuration.configurationTypeId,
                  additionalPropertyToChange
                );
              }}
              updateAdditionRefMethods={(
                method,
                value: string | number | boolean | object | null
              ) => {
                setCustomPropertyOnRef(configuration.configurationTypeId, {
                  propertyName: method,
                  value
                });
              }}
              onMounted={() => {
                onMounted(configuration.configurationTypeId);
              }}
            />
          </>
        )
      };
    }, {});
  }, [
    allConfigurationTypes,
    siteOperationData,
    currentSiteConfigurations,
    onClose,
    getRef,
    register,
    unregister,
    temporaryConfigurationMenuStatus,
    saveConfigurationChages,
    setCustomPropertyOnRef,
    onMounted
  ]);
  const sectionComponent = useMemo(
    () =>
      requiredConfigurationItemsByConfigurationType[
        currentSelectedConfigurationType
      ] ?? <></>,
    [
      currentSelectedConfigurationType,
      requiredConfigurationItemsByConfigurationType,
      temporaryConfigurationMenuStatus,
      getValue(currentSelectedConfigurationType)
    ]
  );
  useEffect(() => {
    (async () => {
      try {
        if (
          (currentConfigurationMenuStatus?.configurationErrorMessage?.length ??
            0) > 0
        ) {
          await checkAndUpdateConfigurationValidationStatus(
            currentSelectedConfigurationType
          );
        }
      } catch {
        updateTemporaryConfigurationMenuStatusById(
          currentSelectedConfigurationType,
          {
            isValid: false,
            hasVisitedConfigurationAtLeastOnce: true
          }
        );
      }
    })();
  }, [currentConfigurationDetails]);

  useEffect(() => {
    const currentId = currentConfigurationMenuStatus?.id;
    const prevId = prevConfigurationMenuStatus?.id;

    if (!temporaryConfigurationMenuStatus || !currentId || !prevId) return;

    const possibleCurrentConfigInputValidation = async () => {
      if (
        currentId &&
        !hasValidatedcurrentConfigurationRef.current[currentId] &&
        !isValidatingCurrentConfigurationRef?.current &&
        prevId &&
        hasValidatedcurrentConfigurationRef.current[prevId] !== undefined &&
        currentConfigurationMenuStatus?.isChildConfigurationRehydrated &&
        currentConfigurationMenuStatus.hasVisitedConfigurationAtLeastOnce
      ) {
        try {
          await checkAndUpdateConfigurationValidationStatus(currentId);
        } catch (error) {
          updateTemporaryConfigurationMenuStatusById(
            currentSelectedConfigurationType,
            {
              isValid: false,
              hasVisitedConfigurationAtLeastOnce: true
            }
          );
        } finally {
          hasValidatedcurrentConfigurationRef.current[currentId] = true;
        }
      }
    };

    possibleCurrentConfigInputValidation();

    return () => {
      if (prevId && currentId !== prevId) {
        updateTemporaryConfigurationMenuStatusById(prevId, {
          isChildConfigurationRehydrated: false,
          hasVisitedConfigurationAtLeastOnce: true
        });
        hasValidatedcurrentConfigurationRef.current[prevId] = false;
      }
    };
  }, [currentConfigurationMenuStatus, prevConfigurationMenuStatus]);

  useEffect(() => {
    if (!!newRequestedConfiguration && !temporaryConfigurationMenuStatus) {
      allConfigurationTypes.forEach((config) => {
        updateTemporaryConfigurationMenuStatusById(config.configurationTypeId, {
          configurationDetails: {
            name: config.configurationName,
            description: `${config.configurationName} description`,
            notes: null
          },
          numberOfErrors: 0,
          isValid: false,
          hasVisitedConfigurationAtLeastOnce: false,
          isChildConfigurationRehydrated: false,
          configurationErrorMessage: "",
          isOpen: true
        });
      });
      if (
        !currentSelectedConfigurationType ||
        currentSelectedConfigurationType !==
          allConfigurationTypes[0].configurationTypeId
      ) {
        updateSelectedConfigurationType(
          allConfigurationTypes[0].configurationTypeId
        );
      }
    }
  }, [newRequestedConfiguration, temporaryConfigurationMenuStatus]);

  useEffect(() => {
    return () => {
      clearAllRefs();
      hasValidatedcurrentConfigurationRef.current = {};
      isValidatingCurrentConfigurationRef.current = false;
    };
  }, []);

  const findNextSectionToOpen = useCallback(
    (configTypeId: string) => {
      return allConfigurationTypes.find((item) => {
        const isChildOfCurrent = item.parentTypeId === configTypeId;
        const isNotCurrent = item.configurationTypeId !== configTypeId;
        const isIncomplete =
          !temporaryConfigurationMenuStatus?.[item.configurationTypeId]
            ?.isValid ||
          !temporaryConfigurationMenuStatus?.[item.configurationTypeId]
            ?.hasVisitedConfigurationAtLeastOnce;

        return (isChildOfCurrent || isNotCurrent) && isIncomplete;
      });
    },
    [allConfigurationTypes, temporaryConfigurationMenuStatus]
  );

  const getAllSubConfigurationsForConfigTypeId = useCallback(
    (
      configurations: NewConfigurationToAddType[],
      initialRequestedConfigurationType: string
    ): NewConfigurationToAddType | null => {
      const configMap = new Map<string, NewConfigurationToAddType[]>();

      configurations.forEach((config: NewConfigurationToAddType) => {
        const parentId = config.parentTypeId;
        if (parentId) {
          if (!configMap.has(parentId)) {
            configMap.set(parentId, []);
          }
          configMap.get(parentId)!.push(config);
        }
      });
      const rootConfigurationType = configurations.find(
        (config) =>
          config.configurationTypeId === initialRequestedConfigurationType
      );
      return rootConfigurationType
        ? buildConfigurationTypeTree(configMap, rootConfigurationType)
        : null;
    },
    [buildConfigurationTypeTree]
  );

  const formatNewConfigurationToAdd = useCallback(
    (selectedEnvironmentsList: string[]) => {
      if (temporaryConfigurationMenuStatus && newRequestedConfiguration) {
        const allConfigurationTypesListWithNoSubConfigurations: NewConfigurationToAddType[] =
          allConfigurationTypes.map((configuration) => {
            const { configurationTypeId, parentTypeId } = configuration;
            const temporaryConfigurationMenuStatusForConfigTypeId =
              temporaryConfigurationMenuStatus[configurationTypeId];

            const { configurationDetails } =
              temporaryConfigurationMenuStatusForConfigTypeId;
            const configurationItemsForConfigTypeId =
              getValue(configurationTypeId)?.getData();

            const formattedConfigurationItemsForConfigTypeId =
              getFormattedRequiredConfigurationItemsList(
                configurationItemsForConfigTypeId,
                selectedEnvironmentsList
              );

            const newConfiguration: NewConfigurationToAddType = {
              configurationTypeId,
              parentTypeId,
              integrationId: null,
              name: configurationDetails.name,
              description: configurationDetails.description,
              notes: configurationDetails.notes,
              configurationItems: formattedConfigurationItemsForConfigTypeId,
              subConfigurations: []
            };

            return newConfiguration;
          });

        return getAllSubConfigurationsForConfigTypeId(
          allConfigurationTypesListWithNoSubConfigurations,
          newRequestedConfiguration.configurationTypeId
        );
      }
      return null;
    },
    [
      allConfigurationTypes,
      newRequestedConfiguration,
      getValue,
      temporaryConfigurationMenuStatus,
      getAllSubConfigurationsForConfigTypeId
    ]
  );

  const configurationMessageContent = useMemo(() => {
    if (!newRequestedConfiguration) return null;

    const subConfigurations = allConfigurationTypes.filter(
      (item) =>
        item.configurationTypeId !==
        newRequestedConfiguration.configurationTypeId
    );

    return (
      <Stack flexDirection="column" color="text.secondary" gap={2}>
        <Typography sx={{ color: "inherit", fontSize: "1.1rem" }}>
          You have added new configuration(s) to current site.
          <br />
          This will create{" "}
          <Box component="span" sx={{ fontWeight: "bold" }}>
            {newRequestedConfiguration.configurationName}
          </Box>
          {` configuration${subConfigurations.length > 0 ? " with the additional subconfiguration(s): " : "."}`}
          {subConfigurations.map((item, i) => (
            <Box
              key={item.configurationTypeId}
              component="span"
              sx={{ fontWeight: "bold" }}
            >
              {item.configurationName}
              {i === subConfigurations.length - 1 ? "." : ", "}
            </Box>
          ))}
        </Typography>
        <Typography sx={{ color: "inherit", fontSize: "1.1rem" }}>
          Do you wish to proceed with the current configuration(s) changes?
        </Typography>
      </Stack>
    );
  }, [allConfigurationTypes, newRequestedConfiguration]);

  const getUserWarningModalContent = useCallback(
    (
      onNewConfigurationCreation: () => Promise<void>
    ): ConfirmActionDialogOptions => {
      return {
        title: "Confirm new configuration addition",
        message: configurationMessageContent,
        actionButtonsDetails: [
          {
            name: "Cancel",
            variant: "text",
            actionCb: closeDialog,
            disabled: isCreatingNewConfigurationLoading
          },
          {
            name: "Create new configuration",
            actionCb: () => {
              void onNewConfigurationCreation();
            },
            isLoading: isCreatingNewConfigurationLoading,
            disabled: isCreatingNewConfigurationLoading
          }
        ]
      };
    },
    [configurationMessageContent, closeDialog]
  );

  const addNewConfigurationToSite = useCallback(async () => {
    try {
      await delay(0);
      const allConfigurationTypes = formatNewConfigurationToAdd(
        siteOperationData.selectedEnvironmentsList
      );

      if (
        allConfigurationTypes &&
        selectedSiteId &&
        newRequestedConfiguration
      ) {
        await createNewConfiguration({
          siteId: selectedSiteId,
          newConfigurationBody: {
            newConfiguration: allConfigurationTypes,
            sendDeployments: false,
            parentConfigurationId:
              initialRequestedConfigurationTypeDetails.parentConfigurationId
          }
        }).unwrap();
        dispatch(
          siteApi.util.invalidateTags([
            {
              type: "site sections",
              id: selectedSiteId
            }
          ])
        );
        dispatch(
          updateUserMessagesList({
            type: "info",
            message: `New configuration ${allConfigurationTypes.name} has been created successfully.`
          })
        );

        queueMicrotask(() => {
          closeDialog();
        });
        onClose();
      }
    } catch {
      dispatch(
        updateUserMessagesList({
          type: "error",
          message: "Error adding new configuration to site."
        })
      );
      closeDialog();
    }
  }, [
    formatNewConfigurationToAdd,
    siteOperationData.selectedEnvironmentsList,
    selectedSiteId
  ]);

  const validateOrSubmitConfigurationChanges = async () => {
    setIsBtnLoading(true);
    try {
      if (checkIfAllSectionsAreValid) {
        const userWarningModalContent = getUserWarningModalContent(
          addNewConfigurationToSite
        );
        openDialog(userWarningModalContent);
        setIsBtnLoading(false);

        return;
      }
      const isCurrentSectionValid =
        await checkAndUpdateConfigurationValidationStatus(
          currentSelectedConfigurationType
        );

      if (!isCurrentSectionValid) {
        setIsBtnLoading(false);
        return;
      }
      const nextSectionToOpen = findNextSectionToOpen(
        currentSelectedConfigurationType
      );
      if (nextSectionToOpen) {
        updateSelectedConfigurationType(nextSectionToOpen.configurationTypeId);
      }
    } finally {
      setIsBtnLoading(false);
    }
  };

  const buttonLabel = checkIfAllSectionsAreValid
    ? "Finish Validation"
    : "Validate and Continue";

  const configurationValidationErrorMessage = useMemo(() => {
    if (!currentConfigurationMenuStatus) return "";
    let errorMessageToDisplay = "";
    const numberOfConfigurationItemsValidationErrors =
      currentConfigurationMenuStatus?.numberOfErrors ?? 0;
    if (
      (!currentConfigurationMenuStatus.isValid &&
        currentConfigurationMenuStatus.isChildConfigurationRehydrated &&
        numberOfConfigurationItemsValidationErrors > 0) ||
      !!currentConfigurationMenuStatus.configurationErrorMessage
    ) {
      if (
        currentConfigurationMenuStatus.configurationErrorMessage?.length != 0 &&
        numberOfConfigurationItemsValidationErrors === 0
      ) {
        errorMessageToDisplay =
          currentConfigurationMenuStatus.configurationErrorMessage;
      } else if (
        numberOfConfigurationItemsValidationErrors > 0 &&
        currentConfigurationMenuStatus.configurationErrorMessage?.length === 0
      ) {
        errorMessageToDisplay = `There ${numberOfConfigurationItemsValidationErrors === 1 ? "is" : "are"} ${numberOfConfigurationItemsValidationErrors} validation ${numberOfConfigurationItemsValidationErrors === 1 ? "error" : "errors"} for the current configuration.`;
      } else {
        errorMessageToDisplay =
          "There are validation issues in the configuration.";
      }
    }

    return errorMessageToDisplay;
  }, [currentConfigurationMenuStatus]);

  if (!newRequestedConfiguration || !temporaryConfigurationMenuStatus)
    return <></>;

  if (!getRef(newRequestedConfiguration?.configurationTypeId))
    return <LoadingSpinner />;

  return (
    <Box
      display="grid"
      gridTemplateColumns="minmax(200px, 300px) 1fr"
      gap={1}
      overflow={"hidden"}
      height={"100%"}
      aria-label="required configuration data container"
    >
      <Box
        mt={2}
        mb={2}
        sx={{
          borderRight: "1px solid rgba(0,0,0,0.2)",
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0
        }}
      >
        <Box mr={1}>
          <TemporaryNewSiteConfigurations
            siteConfiguration={newRequestedConfiguration}
            temporaryConfigurationMenuStatus={temporaryConfigurationMenuStatus}
            currentSelectedConfigurationType={currentSelectedConfigurationType}
            updateSelectedConfigurationType={(id: string) => {
              updateSelectedConfigurationType(id);
            }}
          />
        </Box>
      </Box>
      <Stack flexDirection="column" gap={1} mb={2} sx={{ overflowY: "auto" }}>
        {sectionComponent}
        <Stack flexDirection="column" gap={1} alignItems={"center"}>
          <Typography
            sx={{ color: "error.main", fontSize: "1rem" }}
            aria-label="configuration validation error message"
          >
            {configurationValidationErrorMessage}
          </Typography>
          <Stack
            flexDirection="row"
            gap={3}
            alignItems={"center"}
            alignSelf={"center"}
          >
            <CustomButton
              sx={{ height: 40 }}
              color="secondary"
              variant="contained"
              onClick={onClose}
              disabled={isCreatingNewConfigurationLoading}
            >
              Close
            </CustomButton>
            <CustomButton
              sx={{ width: 200, height: 40 }}
              variant="contained"
              onClick={() => {
                void validateOrSubmitConfigurationChanges();
              }}
              disabled={
                isBtnLoading ||
                isCreatingNewConfigurationLoading ||
                isValidatingCurrentConfigurationRef.current
              }
              loading={isBtnLoading}
            >
              {buttonLabel}
            </CustomButton>
          </Stack>
        </Stack>
      </Stack>
      {isEditSectionModalOpen && currentConfigurationMenuStatus && (
        <EditSiteSectionModal
          isEditSectionModalOpen={isEditSectionModalOpen}
          onClose={() => updateSiteSectionModalStatus(false)}
          isInSiteOperationMode
          configurationDetailsToEdit={
            temporaryConfigurationMenuStatus?.[currentSelectedConfigurationType]
              ?.configurationDetails
          }
          onSubmitChanges={saveConfigurationChages}
        />
      )}
    </Box>
  );
};

export default TemporaryNewConfigurationData;
